import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useSearch } from '@faststore/sdk'
import SearchNotFound from 'src/components/common/SearchNotFound'
import { mark } from 'src/sdk/tests/mark'
import type { ServerCollectionPageQueryQuery } from '@generated/graphql'
import { useLocation } from '@reach/router'
import { RelatedResearchBrand } from 'src/components/brands/RelatedResearch'
import { ContentSEOBrand } from 'src/components/brands/ContentSEO/'
import { RelatedResearch } from 'src/components/sports/RelatedResearch/RelatedResearch'
import { ContentSEO } from 'src/components/sports/ContentSEO'
import { ContextualFilters } from 'src/components/department/ContextualFilters'
import type { ContextualFilter } from 'src/components/department/ContextualFilters'
import FilteredEmptyGallery from 'src/components/common/FilteredEmptyGallery'
import { OptionsMap } from 'src/components/search/SortV2/Sort'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { compareArrays } from 'src/utils/compareArrays'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import type { PlacementResponse } from 'src/utils/placements/fetchPlacementData'
import { PlacementsComponent } from 'src/components/SponsoredProducts/PlacementsComponent'
import { buildProductListObject } from 'src/utils/restructure/analytics/amplitude/buildProductListObject'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import type { InfluencersSectionData } from 'src/components/sections/ProductGalleryV2/ProductGalleryv3'
import { InfluencersSection } from 'src/components/sections/ProductGalleryV2/ProductGalleryv3'
import { filterComponentsByTermAndExcludes } from 'src/components/department/ContextualFilters/utils'

import SortV3 from './SortV3'
import Section from '../../Section'
import ProductResultListV3 from './ProductResultListv3'
import type { SearchResult } from '../types'
import { useProductGalleryAnalytics } from '../hooks/useGalleryEvents'
import GalleryBanner from '../GalleryBanner'
import GalleryFilterV3 from './GalleryFilterV3'
import ProductResultListCollectionPageV3 from './ProductResultListCollectionPagev3'
import type { CollectionCMSComponent } from '../buildComponentList'

import '../product-gallery.scss'

export type GalleryType = 'search' | 'collection' | 'seller' | 'category'

export interface CmsData {
  sections: Array<{
    data: {
      // eslint-disable-next-line
      items: any
    }
    id: string
    name: string
  }>
  status: string
}

interface Props {
  title: string
  categoryTree?: ServerCollectionPageQueryQuery['collection']
  dataSports?: CmsData | null
  searchCMSData?: CmsData | null
  type: GalleryType
  pageType?: string
  bannerMobile?: string
  bannerDesktop?: string
  dataBrand?: CmsData | null
  searchResult?: SearchResult
  placementData?: PlacementResponse & {
    categoryName?: string
    searchTerm?: string
  }
  sphereContents?: Array<{ sphereId: string; sphereHtml: string | null }>
  influencersData?: InfluencersSectionData
  contextualFiltersData?: ContextualFilter[] | null
  alignWithListingHeading?: boolean
}

function ProductGallery({
  title,
  dataSports,
  type,
  bannerMobile,
  bannerDesktop,
  dataBrand,
  pageType,
  searchResult,
  placementData,
  sphereContents,
  influencersData,
  searchCMSData,
  contextualFiltersData,
  alignWithListingHeading = false,
}: Readonly<Props>) {
  const collectionComponentsNames = useMemo(
    () => [
      '[Coleção] Vitrine Inspiracional',
      '[Coleção] Conteúdo Sphere',
      '[Coleção] Banner inline horizontal',
      '[Coleção] Banner inline Vertical',
      '[Coleção 2.0] Banner Full',
      '[Coleção 2.0] Widget Kitlook',
      '[Search] Banner inline horizontal',
      '[Search] Banner inline Vertical',
    ],
    []
  )

  const resolvedInfluencersData = useMemo(() => {
    if (influencersData) {
      return influencersData
    }

    const influencersSection = dataSports?.sections?.find(
      (section) => section.name === '[Coleção 2.0] Influencers'
    )

    return influencersSection?.data as InfluencersSectionData | undefined
  }, [dataSports?.sections, influencersData])

  const {
    state: { term, sort, selectedFacets, page, base },
    itemsPerPage,
  } = useSearch()

  const [isFilterDesktopOpen] = useState<boolean>(true)

  const { pageData } = useGlobalContext()

  const location = useLocation()
  const href = location?.href ?? ''

  const isSearchPage = href?.includes('pesquisa')

  const totalCount = searchResult?.filteredRecords ?? 0

  const oldSort = useRef(sort)
  const oldFilters = useRef(selectedFacets)

  const displaySponsoredProducts = type === 'search' || type === 'category'

  useProductGalleryAnalytics({
    searchResult,
    term,
    type,
    title,
    totalCount,
    page,
    sort,
    selectedFacets,
    isSearchPage,
    base,
    oldSort: oldSort.current,
  })

  const getPageTitle = (str: string) => str?.replace(/-/g, ' ') ?? ''

  const sendSortOrderChangedEvent = useCallback(() => {
    dispatchAmplitudeEvent({
      eventName: 'Sort Order Changed',
      eventData: {
        ...getStandardObject(pageData),
        ...buildProductListObject({
          listName: `Página de lista de produtos: ${getPageTitle(title)}`,
          listType: 'Página de lista de produtos',
          productsCount: searchResult?.filteredRecords ?? 0,
          base,
          page: page === 0 ? 1 : page,
          selectedFacets,
          sort: OptionsMap[sort as keyof typeof OptionsMap],
        }),

        'new sort order': OptionsMap[sort as keyof typeof OptionsMap],
        'old sort order':
          OptionsMap[oldSort.current as keyof typeof OptionsMap],
      },
    })

    oldSort.current = sort
  }, [
    base,
    page,
    pageData,
    searchResult?.filteredRecords,
    selectedFacets,
    sort,
    title,
  ])

  const sendFilterAppliedEvent = useCallback(() => {
    dispatchAmplitudeEvent({
      eventName: 'Filter Applied',
      eventData: {
        ...getStandardObject(pageData),
        ...buildProductListObject({
          listName: `Página de lista de produtos: ${getPageTitle(title)}`,
          listType: 'Página de lista de produtos',
          productsCount: searchResult?.filteredRecords ?? 0,
          base,
          page: page === 0 ? 1 : page,
          selectedFacets,
          sort: OptionsMap[sort as keyof typeof OptionsMap],
        }),
      },
    })

    oldFilters.current = selectedFacets
  }, [
    base,
    page,
    pageData,
    searchResult?.filteredRecords,
    selectedFacets,
    sort,
    title,
  ])

  const collectionComponentsConfig = useMemo(() => {
    if (searchResult && type === 'collection') {
      return dataSports?.sections?.filter((section) =>
        collectionComponentsNames.includes(section.name)
      ) as CollectionCMSComponent[] | undefined
    }

    if (type === 'search') {
      const searchComponents =
        (searchCMSData?.sections?.filter((section) =>
          collectionComponentsNames.includes(section.name)
        ) as CollectionCMSComponent[] | undefined) ?? []

      const isSearchBanner = (name: string) =>
        name === '[Search] Banner inline horizontal' ||
        name === '[Search] Banner inline Vertical'

      const searchBanners = searchComponents.filter((component) =>
        isSearchBanner(component.name)
      )

      if (!searchBanners || searchBanners.length === 0) {
        return undefined
      }

      const filteredSearchBanners = filterComponentsByTermAndExcludes(
        getPageTitle(term ?? title),
        searchBanners
      )

      return [...filteredSearchBanners]
    }

    return undefined
  }, [
    collectionComponentsNames,
    dataSports,
    searchCMSData,
    searchResult,
    term,
    title,
    type,
  ])

  useEffect(() => {
    if (oldSort.current !== sort) {
      sendSortOrderChangedEvent()
    }

    if (!compareArrays(oldFilters.current, selectedFacets)) {
      sendFilterAppliedEvent()
    }
  }, [
    selectedFacets,
    sort,
    page,
    sendSortOrderChangedEvent,
    sendFilterAppliedEvent,
    searchResult,
  ])

  if (searchResult?.products.length === 0) {
    return (
      <Section className="w-full mx-auto">
        {selectedFacets.length > 0 ? (
          <FilteredEmptyGallery />
        ) : (
          <SearchNotFound term={title} />
        )}
      </Section>
    )
  }

  return (
    <Section
      data-testid="product-gallery"
      className={`product-listing layout__content-full ${
        alignWithListingHeading
          ? ''
          : dataSports
          ? 'restructure-small-desktop:mt-12 mt-14 md:mt-20'
          : 'restructure-small-desktop:mt-12'
      }`}
    >
      <GalleryBanner
        bannerMobile={bannerMobile}
        bannerDesktop={bannerDesktop}
      />
      <div className="px-4 sm:px-8 w-full flex flex-col items-stretch justify-center md:max-w-[1680px] md:mx-auto md:px-[42px]">
        <div
          className={`max-w-full w-full min-w-0 ${
            !isFilterDesktopOpen ? 'full !bg-inherit !h-auto' : ''
          }`}
        >
          <ContextualFilters
            term={getPageTitle(term ?? title)}
            productListName={pageData.currentPageName}
            sortOrder={OptionsMap[sort as keyof typeof OptionsMap]}
            numberOfProducts={searchResult?.filteredRecords ?? 0}
            initialData={contextualFiltersData}
          />
          <div className="flex flex-col md:flex-row md:items-center min-w-0 w-full md:gap-4">
            {isFilterDesktopOpen && (
              <div
                id="product-listing__filters-bar"
                className="w-full min-w-0 flex-1 md:w-0"
              >
                <GalleryFilterV3
                  searchResult={searchResult}
                  totalCount={totalCount}
                />
              </div>
            )}
            <div className="justify-start md:justify-end flex items-center product-listing__results-wrapper-top shrink-0 w-full md:w-auto md:max-w-[249px] md:pl-1">
              <div className="flex items-center md:ml-auto mr-0 product-listing__sort">
                <span
                  data-testid="total-product-count"
                  className="text-sm text-restructure-primary font-medium capitalize pr-5 border-r border-checkoutDefaultBorder whitespace-nowrap"
                >
                  {`${totalCount.toLocaleString()} itens`}
                </span>
                <div className="pl-5">
                  <SortV3 />
                </div>
              </div>
            </div>
          </div>

          {displaySponsoredProducts && placementData && (
            <PlacementsComponent placementData={placementData} />
          )}
          {collectionComponentsConfig ? (
            <ProductResultListCollectionPageV3
              products={searchResult?.products}
              page={page}
              title={title ?? term}
              totalProducts={searchResult?.filteredRecords ?? 0}
              registerPerPage={itemsPerPage}
              type={pageType}
              collectionCMSData={collectionComponentsConfig}
              sphereContents={sphereContents}
            />
          ) : (
            <ProductResultListV3
              products={searchResult?.products}
              page={page}
              title={title ?? term}
              totalProducts={searchResult?.filteredRecords ?? 0}
              registerPerPage={itemsPerPage}
              type={pageType}
            />
          )}

          {dataSports && <RelatedResearch dataCMS={dataSports} />}
          {dataSports && <ContentSEO dataCMS={dataSports} />}
          {dataBrand && <RelatedResearchBrand dataCMS={dataBrand} />}
          {dataBrand && <ContentSEOBrand dataCMS={dataBrand} />}
        </div>
      </div>

      {resolvedInfluencersData && (
        <InfluencersSection {...resolvedInfluencersData} />
      )}
    </Section>
  )
}

ProductGallery.displayName = 'ProductGallery'

export default mark(ProductGallery)
